import React from "react";
import { PARTS as DAMAGE_PARTS, STATUS as DAMAGE_STATUS } from "@/components/VehicleDamageMap";
import { COLORS } from "./primitives";

// Read-only Schadenbilanz-Diagramm für die Dokumentation (Vorschäden-Seite).
// Rendert das Auto-Schemabild mit farbigen Status-Overlays + Pfeile, ohne
// Interaktion (Klicken/Editieren).
export function DamageDiagramReadOnly({ preDamages, arrows }) {
  // Multi-Status-Support: ein Teil kann mehrere Statuses haben.
  // Für die visuelle Darstellung im Diagramm wählen wir die "wichtigste"
  // (Priorität: neu > alt > vor) und rendern in deren Farbe.
  const PRIORITY = ["neu", "alt", "vor"];
  const pickStatus = (v) => {
    const arr = Array.isArray(v?.statuses) && v.statuses.length
      ? v.statuses
      : (v?.status ? [v.status] : []);
    return PRIORITY.find((s) => arr.includes(s)) || null;
  };
  const damageEntries = Object.entries(preDamages || {})
    .map(([id, v]) => {
      const part = DAMAGE_PARTS.find((p) => p.id === id);
      const status = pickStatus(v);
      if (!part || !status) return null;
      return { ...part, status, note: v?.note || "" };
    })
    .filter(Boolean);
  return (
    <div style={{
      position: "relative", width: "100%", aspectRatio: "900 / 1400",
      maxWidth: "90mm",
    }}>
      <img
        src="/car-parts-clean-v5.png"
        alt="Fahrzeug-Schadenbilanz"
        crossOrigin="anonymous"
        style={{ width: "100%", height: "100%", display: "block", objectFit: "contain" }}
      />
      {damageEntries.map((e) => {
        const s = DAMAGE_STATUS[e.status];
        if (!s) return null;
        return (
          <div
            key={e.id}
            style={{
              position: "absolute",
              left: `${e.x - e.w / 2}%`,
              top: `${e.y - e.h / 2}%`,
              width: `${e.w}%`,
              height: `${e.h}%`,
              background: s.tint,
              border: `1.5pt solid ${s.bg}`,
              borderRadius: "3px",
            }}
          />
        );
      })}
      {(arrows || []).map((a, i) => (
        <div
          key={a.id || i}
          style={{
            position: "absolute",
            left: `${a.x}%`,
            top: `${a.y}%`,
            transform: "translate(-50%, -50%)",
            width: "5mm", height: "5mm",
            background: DAMAGE_STATUS[a.status]?.bg || "#dc2626",
            color: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "7.5pt",
            fontWeight: 700,
            border: "1pt solid white",
            boxShadow: "0 0 0 0.5pt rgba(0,0,0,0.3)",
          }}
        >
          {i + 1}
        </div>
      ))}
    </div>
  );
}

// Damage-Listen gruppiert nach Status (Neuschäden, Altschäden, Vorschäden)
export function DamageList({ title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginTop: "4mm" }}>
      <div style={{
        fontSize: "8pt", fontWeight: 700, color,
        letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1.5mm",
      }}>{title} · {items.length}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {items.map((it, i) => (
          <li key={i} style={{
            display: "flex", justifyContent: "space-between", gap: "6mm",
            padding: "1.8mm 0", borderBottom: `0.3pt solid ${COLORS.border}`,
            fontSize: "9.5pt", color: COLORS.body,
          }}>
            <span style={{ fontWeight: 600, color: COLORS.ink }}>
              {it.n ? `${it.n}. ` : ""}{it.label}
            </span>
            {it.note && (
              <span style={{ color: COLORS.mute, fontStyle: "italic", textAlign: "right", flex: 1 }}>
                {it.note}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}


// Auto-generierte Zusammenfassung aller in der Schadengrafik markierten
// Schäden — wird in der Schadenfeststellung-Sektion direkt unter der
// Schadenbeschreibung gerendert. Multi-Status pro Teil (statuses-Array)
// und Legacy-Single-Status (status-Feld) werden beide unterstützt.
export function DamageSelectionSummary({ preDamages, arrows }) {
  const items = Object.entries(preDamages || {})
    .map(([id, v]) => {
      const part = DAMAGE_PARTS.find((p) => p.id === id) || (v?.label ? { id, label: v.label } : null);
      if (!part || !v) return null;
      const statuses = Array.isArray(v.statuses) && v.statuses.length
        ? v.statuses
        : (v.status ? [v.status] : []);
      if (statuses.length === 0) return null;
      return { part, statuses, note: v.note || "" };
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  // Gruppiere nach Status (Neuschaden, Vorschaden, Altschaden)
  const buckets = { neu: [], vor: [], alt: [] };
  for (const it of items) {
    for (const s of it.statuses) {
      if (buckets[s]) buckets[s].push(it);
    }
  }
  const ORDER = ["neu", "alt", "vor"];

  return (
    <div style={{
      marginTop: "5mm",
      padding: "4mm 5mm",
      background: COLORS.light,
      borderLeft: `2pt solid ${COLORS.accent}`,
    }}>
      <div style={{
        fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
        letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "2mm",
      }}>
        Erfasste Schäden aus der Schadengrafik
      </div>
      {ORDER.map((statusKey) => {
        const bucket = buckets[statusKey];
        if (!bucket.length) return null;
        const meta = DAMAGE_STATUS[statusKey];
        return (
          <div key={statusKey} style={{ marginTop: "2mm" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: "2mm",
              fontSize: "9pt", fontWeight: 700, color: meta.bg, marginBottom: "1mm",
            }}>
              <span style={{
                display: "inline-block", width: "7pt", height: "7pt",
                borderRadius: "50%", background: meta.bg,
              }} />
              {meta.label} ({bucket.length})
            </div>
            <ul style={{ margin: 0, paddingLeft: "5mm", fontSize: "9.5pt", color: COLORS.body }}>
              {bucket.map((it, i) => (
                <li key={i} style={{ marginBottom: "0.5mm" }}>
                  <span style={{ fontWeight: 600, color: COLORS.ink }}>{it.part.label}</span>
                  {it.note ? <span style={{ color: COLORS.mute }}> — {it.note}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
